/** Users · shared pipeline vocabulary — requisition state → stage tracker + pill classes
 *  (7 stages posted, 5 for upload/direct where Posted+Sourcing don't exist).
 *  One place so Dashboard, Requisitions and the funnel all agree on stage math. */
import type { ReactNode } from "react";
import type { EntryMode, PipelineStatus, RequisitionState } from "@talenthop/shared";
import { Check } from "../../components/icons.js";

export const PIPELINE_STAGES = [
  "Requested",
  "Posted",
  "Sourcing",
  "Screening",
  "Interviewing",
  "Scoring",
  "Approval",
] as const;

/** Map every RequisitionState onto the 7 proto tracker stages (0-based). */
const STAGE_INDEX: Record<RequisitionState, number> = {
  draft: 0,
  posted: 1,
  sourcing: 2,
  collecting: 2,
  screening: 3,
  shortlisted: 3,
  inviting: 4,
  scheduling: 4,
  interviewing: 4,
  aggregating: 5,
  report_ready: 6,
  awaiting_requester: 6,
  live_round: 6,
  finalized: 6,
  closed: 6,
  on_hold: 2,
};

export function stageIndex(state: RequisitionState, entryMode?: EntryMode): number {
  // Uploaded/direct requests skip Posted + Sourcing, so `collecting` (a post-mode Sourcing state)
  // has no matching stage there — park them on Interviewing (4) so the tracker shows a live step.
  if (state === "collecting" && entryMode && entryMode !== "post") return 4;
  return STAGE_INDEX[state] ?? 0;
}

/** Uploaded/direct requests skip Auto-posted + Sourcing (proto `data-skip="1,2"`). */
export function skippedStages(entryMode: EntryMode): number[] {
  return entryMode === "post" ? [] : [1, 2];
}

/** Pill class per requisition state — positive for done, teal for in-flight, neutral otherwise. */
export function statePill(state: RequisitionState): string {
  if (state === "finalized" || state === "report_ready") return "pill v";
  if (state === "awaiting_requester" || state === "live_round" || state === "on_hold") return "pill r";
  if (state === "closed" || state === "draft") return "pill n";
  return "pill t";
}

/** Theme 1 list/table stage badge for a requisition: the stage the role is actually at, in
 *  sentence case, with the status palette — grey = draft/closed/on hold (nothing running),
 *  blue = in progress, amber = waiting on a human, green = finished. Used by the Dashboard and
 *  Requirements lists; `statePill` above is left as-is for the pages that already use it. */
const STAGE_LABEL: Record<string, { label: string; cls: string }> = {
  draft: { label: "Draft", cls: "pill n" },
  posted: { label: "Posted", cls: "pill pv" },
  sourcing: { label: "Sourcing", cls: "pill pv" },
  collecting: { label: "Collecting", cls: "pill pv" },
  screening: { label: "Screening", cls: "pill pv" },
  shortlisted: { label: "Shortlisted", cls: "pill pv" },
  inviting: { label: "Inviting", cls: "pill pv" },
  scheduling: { label: "Scheduling", cls: "pill pv" },
  interviewing: { label: "Interviewing", cls: "pill pv" },
  aggregating: { label: "Scoring", cls: "pill pv" },
  live_round: { label: "Live round", cls: "pill pv" },
  report_ready: { label: "Report ready", cls: "pill r" },
  awaiting_requester: { label: "Awaiting you", cls: "pill r" },
  finalized: { label: "Finalized", cls: "pill v" },
  closed: { label: "Closed", cls: "pill n" },
  archived: { label: "Archived", cls: "pill n" },
  on_hold: { label: "On hold", cls: "pill n" },
};

export function reqStageBadge(state: string | undefined): { label: string; cls: string } {
  const s = (state ?? "").toLowerCase();
  return STAGE_LABEL[s] ?? { label: s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ") : "Open", cls: "pill pv" };
}

/** Budget as the customer entered it, in ONE format everywhere ("₹24L – ₹32L", "$120k – $155k").
 *  Returns null when no budget was set — the list shows "Not set" rather than a figure guessed
 *  from the job title (requisitionFormat.getRequisitionBudget invents one; that is not shown). */
export function budgetLabel(req: {
  region?: string;
  config?: {
    budgetMin?: number | string;
    budgetMax?: number | string;
    salaryMin?: number;
    salaryMax?: number;
    salaryCurrency?: string;
    currency?: string;
  };
}): string | null {
  const c = req.config ?? {};
  const rawMin = c.salaryMin ?? c.budgetMin;
  const rawMax = c.salaryMax ?? c.budgetMax;
  if (rawMin == null && rawMax == null) return null;
  const num = (v: number | string | undefined): number | null => {
    if (v == null || v === "") return null;
    if (typeof v === "number") return Number.isFinite(v) && v > 0 ? v : null;
    const n = Number(v.replace(/[^0-9.]/g, ""));
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  const min = num(rawMin);
  const max = num(rawMax);
  const textHint = `${String(c.budgetMin ?? "")}${String(c.budgetMax ?? "")}`;
  const cur = (c.salaryCurrency ?? c.currency ?? "").toUpperCase();
  const inr = cur === "INR" || textHint.includes("₹") || (!cur && !textHint.includes("$") && req.region === "in-central");
  const fmt = (v: number): string => {
    if (inr) {
      if (v >= 1e7) return `₹${+(v / 1e7).toFixed(1)}Cr`;
      if (v >= 1e5) return `₹${+(v / 1e5).toFixed(1)}L`;
      return `₹${v.toLocaleString("en-IN")}`;
    }
    const sym = cur === "EUR" ? "€" : cur === "GBP" ? "£" : "$";
    return v >= 1000 ? `${sym}${Math.round(v / 1000)}k` : `${sym}${v}`;
  };
  if (min != null && max != null) return `${fmt(min)} – ${fmt(max)}`;
  if (min != null) return `From ${fmt(min)}`;
  if (max != null) return `Up to ${fmt(max)}`;
  // Unparseable free text: show what was typed, never a guess.
  return [rawMin, rawMax].filter((v) => v != null && v !== "").join(" – ") || null;
}

/** Plain-language interview conversion: "3 of 5 matched" style, never a >100% rate. */
export function interviewedOfMatched(interviews: number, matches: number): string | null {
  if (matches <= 0 || interviews <= 0) return null;
  if (interviews > matches) return "incl. direct adds";
  return `${Math.round((interviews / matches) * 100)}% of matched`;
}

/**
 * Pill class per recruiter pipeline-status TAG (migration 0077) — a completely separate
 * vocabulary from `statePill`/`RequisitionState`/`CandidacyState` above: this is the free-form
 * organizational label, not a system-driven state. Colors are chosen for what the label MEANS to
 * a recruiter scanning the table, not for any relation to the system state machine — "rejected"
 * reads as `pill f` (critical/red) here purely as a visual label, the same as an authenticity
 * "failed" band would, but it never rejects anyone (see PIPELINE_STATUSES' doc comment,
 * packages/shared/src/types.ts).
 */
export function pipelineStatusPillClass(status: PipelineStatus | null | undefined): string {
  switch (status) {
    case "hired":
      return "pill v";
    case "shortlisted":
      return "pill t";
    case "needs_review":
      return "pill r";
    case "on_hold":
      return "pill pv";
    case "rejected":
      return "pill f";
    case "applied":
    default:
      return "pill n"; // includes untagged (null/undefined)
  }
}

/**
 * Roles trusted to see/edit how a requisition's candidates are actually judged — the per-
 * requisition custom judge-prompt editor (RequisitionDetail.tsx) and the "copy the exact AI
 * prompt" action (Review.tsx) share this one bar, both gated additionally on the org's
 * `customPromptEnabled` flag (migration 0075). Mirrored server-side in
 * apps/api/src/functions/requisitionPrompt.ts and candidacyJudgeCall.ts — client-side use is UX
 * only (hides a control that would just 403), the API is the real gate.
 */
export const PROMPT_EDITOR_ROLES = new Set(["super_admin", "hiring_manager"]);

export const REGION_LABEL: Record<string, string> = {
  "in-central": "India",
  "us-east": "US East",
  "eu-west": "EU West",
};

interface StageTrackerProps {
  state: RequisitionState;
  entryMode: EntryMode;
  /** Requisitions past report_ready render the last stage as done, not "now". */
  done?: boolean;
}

/** Full-size end-to-end tracker (proto `.tracker`): done ✓ / now glow / todo number.
 *  Skipped stages are FILTERED OUT, not struck through (#38): upload/direct requests show a
 *  five-stage journey with dots numbered 1–5 — RequisitionDetail's header already explains why
 *  Posted/Sourcing are absent, so ghost steps with a rail through them said nothing new. */
export function StageTracker({ state, entryMode, done }: StageTrackerProps): ReactNode {
  const skip = new Set(skippedStages(entryMode));
  const stages = PIPELINE_STAGES.map((label, abs) => ({ label, abs })).filter(
    ({ abs }) => !skip.has(abs),
  );
  // "now" in filtered coordinates: stageIndex() speaks absolute stage numbers, so count how many
  // surviving stages precede it — that keeps the glow on the right dot when 1–2 are gone.
  const absCur = stageIndex(state, entryMode);
  const cur = stages.filter(({ abs }) => abs < absCur).length;
  const complete = done ?? (state === "finalized" || state === "closed");
  return (
    <div className="tracker">
      {stages.map(({ label }, i) => {
        const isDone = i < cur || (complete && i <= cur);
        const cls = isDone ? "tstep done" : i === cur ? "tstep now" : "tstep";
        return (
          <div key={label} className={cls}>
            <div className="dot">{isDone ? <Check /> : i + 1}</div>
            <div className="tl">{label}</div>
          </div>
        );
      })}
    </div>
  );
}
